'use client';

import { MessageMarkerButton } from '@/@admin/components/MessageMarkerButton';
import { STUDIO_AI_NAME } from '@/@studio-ai/constants';
import { MessageActionsProps } from '@/@studio-ai/props';
import {
    EXPORT_DATA_TOOLTIP,
    extractMessageText,
    isExportInProgress,
    startQueryDataExport,
} from '@/@studio-ai/utils';
import { SubmitMessageModal } from '@/components/ui/organisms/SubmitMessageModal';
import { isFirstDataAnswer } from '@/@studio-ai/utils/first-data-answer';
import { hasViewAsSession } from '@/@subsidiary/view-as-store';
import { EVENTS, tracking } from '@/lib/features/tracking';
import { useSession } from '@/providers/enhanced-session';
import { formatDateTime, formatRelativeDate } from '@/lib/utils/formatting';
import { Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { DarkTooltip } from './dark-tooltip';

export function MessageActions({
    messageContent,
    showFeedback = false,
    existingFeedback,
    messageId,
    currentConversationId,
    currentThreadId,
    submitFeedback,
    startNewChat,
    messageMarkerProps,
    canExportData,
    timestamp,
}: MessageActionsProps) {
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [isPositiveFeedback, setIsPositiveFeedback] = useState(true);
    const [isCopied, setIsCopied] = useState(false);
    const { data: session } = useSession();
    const isInternal = session?.user?.isInternal ?? false;

    const canSubmitFeedback =
        showFeedback &&
        !existingFeedback &&
        messageId != null &&
        currentConversationId != null &&
        currentThreadId != null &&
        submitFeedback != null;

    const handleCopy = async () => {
        const text = extractMessageText(messageContent);
        if (!text) return;

        try {
            await navigator.clipboard.writeText(text);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        } catch {
            toast.error('Failed to copy to clipboard', {
                position: 'top-right',
                autoClose: 2000,
            });
        }
    };

    const handleFeedbackPress = (positive: boolean) => {
        setIsPositiveFeedback(positive);
        setIsFeedbackModalOpen(true);
    };

    const handleExportFullData = useCallback(() => {
        if (!canExportData || isExportInProgress()) return;
        startQueryDataExport(currentConversationId, messageId);
    }, [canExportData, currentConversationId, messageId]);

    const handleSubmitFeedback = async (data: { selectValue: string; messageText: string }) => {
        if (!canSubmitFeedback) return;

        try {
            await submitFeedback({
                conversationId: currentConversationId,
                threadId: currentThreadId,
                messageId,
                isPositive: isPositiveFeedback,
                feedbackMessage: data.messageText,
                category: data.selectValue,
            });

            // Phase 0 of DANI memory (A2): the guardrail reads clients' thumbs-down on
            // first data answers. Staff and View-as are left out, as in A1.
            if (!isInternal && !hasViewAsSession()) {
                tracking.event(EVENTS.DANI_ANSWERS.ANSWER_FEEDBACK, {
                    conversationId: currentConversationId,
                    messageId,
                    isPositive: isPositiveFeedback,
                    isFirstDataAnswer: isFirstDataAnswer(currentConversationId, messageId),
                    isRecall: false,
                });
            }

            toast.success('Feedback submitted successfully', {
                position: 'top-right',
                autoClose: 3000,
                hideProgressBar: false,
            });
        } catch (error) {
            toast.error('Feedback submission failed, please try again.', {
                position: 'top-right',
                autoClose: 3000,
                hideProgressBar: false,
            });
            tracking.errorWithSource(error as Error, 'MessageActions.handleSubmitFeedback', {
                messageId,
                isPositive: isPositiveFeedback,
            });
        } finally {
            setIsFeedbackModalOpen(false);
        }
    };

    const feedbackHeader = useMemo(() => {
        return isPositiveFeedback ? 'What went well?' : 'What can we improve?';
    }, [isPositiveFeedback]);

    const feedbackSubheader = useMemo(() => {
        return isPositiveFeedback
            ? `Thank you for your feedback! We appreciate your help in making ${STUDIO_AI_NAME} better.`
            : "We're sorry to hear that. Please let us know how we can improve.";
    }, [isPositiveFeedback]);

    return (
        <div className="mt-1 w-full">
            <div className="flex items-center gap-0">
                {showFeedback && (
                    <>
                        <Button
                            isIconOnly
                            radius="full"
                            variant="ghost"
                            color="default"
                            isDisabled={!!existingFeedback}
                            onPress={() => handleFeedbackPress(true)}
                            className={`h-6 w-6 min-w-6 border-none ${
                                existingFeedback?.isPositive
                                    ? 'text-success-500 opacity-100'
                                    : existingFeedback
                                      ? 'opacity-40'
                                      : ''
                            }`}
                        >
                            <Icon icon="lucide:thumbs-up" width={14} height={14} />
                        </Button>
                        <Button
                            isIconOnly
                            radius="full"
                            variant="ghost"
                            color="default"
                            isDisabled={!!existingFeedback}
                            onPress={() => handleFeedbackPress(false)}
                            className={`h-6 w-6 min-w-6 border-none ${
                                existingFeedback && !existingFeedback.isPositive
                                    ? 'text-danger-500 opacity-100'
                                    : existingFeedback
                                      ? 'opacity-40'
                                      : ''
                            }`}
                        >
                            <Icon icon="lucide:thumbs-down" width={14} height={14} />
                        </Button>
                    </>
                )}
                <DarkTooltip content={isCopied ? 'Copied!' : 'Copy text from response'} size="sm">
                    <Button
                        isIconOnly
                        radius="full"
                        variant="ghost"
                        color="default"
                        onPress={handleCopy}
                        className="h-6 w-6 min-w-6 border-none"
                    >
                        <Icon
                            icon={isCopied ? 'lucide:check' : 'lucide:copy'}
                            width={14}
                            height={14}
                        />
                    </Button>
                </DarkTooltip>
                {showFeedback && startNewChat && (
                    <DarkTooltip content="Start new chat from this response" size="sm">
                        <Button
                            isIconOnly
                            radius="full"
                            variant="ghost"
                            color="default"
                            onPress={startNewChat}
                            className="h-6 w-6 min-w-6 border-none"
                        >
                            <Icon icon="lucide:trending-up-down" width={14} height={14} />
                        </Button>
                    </DarkTooltip>
                )}
                {canExportData && (
                    <DarkTooltip content={EXPORT_DATA_TOOLTIP} size="sm">
                        <Button
                            isIconOnly
                            radius="full"
                            variant="ghost"
                            color="default"
                            onPress={handleExportFullData}
                            className="h-6 w-6 min-w-6 border-none"
                        >
                            <Icon icon="lucide:download" width={14} height={14} />
                        </Button>
                    </DarkTooltip>
                )}
                {messageMarkerProps && (
                    <MessageMarkerButton
                        message={messageMarkerProps.message}
                        conversationId={messageMarkerProps.conversationId}
                        onMarkerUpdated={messageMarkerProps.onMarkerUpdated}
                    />
                )}
                {timestamp && (
                    <DarkTooltip content={formatDateTime(timestamp)} size="sm">
                        <span className="ml-2 cursor-default select-none text-small text-foreground/40 opacity-0 transition-opacity group-hover/message:opacity-100">
                            {formatRelativeDate(timestamp)}
                        </span>
                    </DarkTooltip>
                )}
            </div>
            {canSubmitFeedback && (
                <SubmitMessageModal
                    isOpen={isFeedbackModalOpen}
                    onOpenChange={setIsFeedbackModalOpen}
                    onSubmit={handleSubmitFeedback}
                    header={feedbackHeader}
                    subheader={feedbackSubheader}
                    messagePlaceholder="Please provide feedback on your experience..."
                />
            )}
        </div>
    );
}
